import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import AppointmentForm from "./appointment-form";

interface BookAppointmentPopupProps {
  alwaysShow?: boolean;
}

export default function BookAppointmentPopup({
  alwaysShow = false,
}: BookAppointmentPopupProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Check if we should show the popup
    const shouldShowPopup = () => {
      // If alwaysShow is true, always show the popup (for development)
      if (alwaysShow) return true;

      const lastShownAt = localStorage.getItem("bookAppointmentPopupLastShown");
      const lastSubmittedAt = localStorage.getItem(
        "appointmentLastSubmittedAt",
      );

      // If never shown before, show it
      if (!lastShownAt) return true;

      // Check if it was shown today
      const lastShownDate = new Date(lastShownAt).toDateString();
      const todayDate = new Date().toDateString();

      // Don't show if it was already shown today
      if (lastShownDate === todayDate) return false;

      // Don't show if user submitted an appointment today
      if (lastSubmittedAt) {
        const lastSubmittedDate = new Date(lastSubmittedAt).toDateString();
        if (lastSubmittedDate === todayDate) return false;
      }

      return true;
    };

    // Setup exit intent detection
    const handleExitIntent = (e: MouseEvent) => {
      // Detect if mouse is leaving through the top of the page
      if (e.clientY <= 0 && shouldShowPopup()) {
        setOpen(true);
        // Update last shown timestamp
        localStorage.setItem(
          "bookAppointmentPopupLastShown",
          new Date().toISOString(),
        );
      }
    };

    // Add exit intent listener
    document.addEventListener("mouseleave", handleExitIntent);

    // For development, immediately show if alwaysShow is true
    if (alwaysShow) {
      setOpen(true);
    }

    // Clean up
    return () => {
      document.removeEventListener("mouseleave", handleExitIntent);
    };
  }, [alwaysShow]);

  // Override the AppointmentForm's submit handler to track submission
  const handleFormSubmitted = () => {
    // Store the submission timestamp in localStorage
    localStorage.setItem(
      "appointmentLastSubmittedAt",
      new Date().toISOString(),
    );
    // Close the dialog
    setOpen(false);
  };

  // Create a wrapper component to listen for the form submission
  const FormWithSubmissionTracking = () => {
    const originalForm = <AppointmentForm />;

    // Listen for the form submission event
    useEffect(() => {
      const handleSubmit = () => {
        handleFormSubmitted();
      };

      // Listen for a custom event that will be dispatched when the form is submitted
      document.addEventListener("appointment-submitted", handleSubmit);

      return () => {
        document.removeEventListener("appointment-submitted", handleSubmit);
      };
    }, []);

    return originalForm;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Limited Time Offer</DialogTitle>
          <DialogDescription>
            Book today and receive a no-charge consultation! Fill out this form
            to secure your appointment and take advantage of this special offer.
          </DialogDescription>
        </DialogHeader>
        <FormWithSubmissionTracking />
      </DialogContent>
    </Dialog>
  );
}
